import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Accordion,
  AccordionItem,
  Spinner,
  Input,
  Divider,
} from "@heroui/react";
import {
  Database,
  Table,
  MagnifyingGlass,
  Copy,
  CheckCircle,
  ArrowRight,
  Tree,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

interface DatabaseTable {
  tableName: string;
  columns: DatabaseColumn[];
  relations?: DatabaseRelation[];
  relationshipPaths?: RelationshipPath[];
}

interface DatabaseColumn {
  columnName: string;
  dataType: string;
  isNullable: boolean;
  defaultValue?: string;
  isPrimaryKey: boolean;
  isForeignKey: boolean;
}

interface DatabaseRelation {
  relationName: string;
  targetTable: string;
  relationType: "ManyToOne" | "OneToMany" | "OneToOne" | "ManyToMany";
  isNullable: boolean;
}

interface RelationshipPath {
  path: string[];
  targetTable: string;
  displayPath: string;
}

interface SchemaData {
  tables: string[];
  detailedTables: DatabaseTable[];
}

interface DynamicPlaceholderBrowserProps {
  onPlaceholderSelect?: (placeholder: string) => void;
}

const DynamicPlaceholderBrowser: React.FC<DynamicPlaceholderBrowserProps> = ({
  onPlaceholderSelect,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [schemaData, setSchemaData] = useState<SchemaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedPlaceholder, setCopiedPlaceholder] = useState<string | null>(
    null,
  );
  const isFetchingRef = useRef(false);
  const hasDataRef = useRef(false);

  const fetchSchema = useCallback(async () => {
    if (isFetchingRef.current) {
      console.log("Schema fetch already in progress, skipping...");
      return;
    }

    if (hasDataRef.current) {
      console.log("Schema already loaded, skipping fetch...");
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      console.log("Fetching database schema...");

      let response = await fetchWithAuth(
        `${API_URL}/document-generation/schema`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        console.log("Full schema failed, trying basic schema...");
        toast.info(
          "Laden van volledige schema mislukt, proberen basis schema...",
        );

        response = await fetchWithAuth(
          `${API_URL}/document-generation/schema/basic`,
          { method: "GET" },
          session,
        );
      }

      if (response.ok) {
        const data = await response.json();
        console.log("Schema fetched successfully:", data);
        console.log(
          "🔍 DEBUG: Raw schema data:",
          JSON.stringify(data, null, 2),
        );
        console.log("🔍 DEBUG: Detailed tables:", data.detailedTables);

        // Debug each table's relations
        if (data.detailedTables) {
          data.detailedTables.forEach((table: any) => {
            console.log(`🔍 DEBUG: Table ${table.tableName}:`, {
              columns: table.columns?.length || 0,
              relations: table.relations?.length || 0,
              relationDetails: table.relations,
            });
          });
        }

        setSchemaData({
          tables: data.tables ? Object.keys(data.tables) : [],
          detailedTables: data.detailedTables || [],
        });
        hasDataRef.current = true;

        if (data.message && data.message.includes("Basic")) {
          toast.warning(
            "Basis schema geladen - sommige details zijn mogelijk niet beschikbaar",
          );
        }
      } else {
        throw new Error("Failed to fetch schema");
      }
    } catch (error) {
      console.error("Error fetching schema:", error);
      toast.error("Kon database schema niet laden");
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      fetchSchema();
    }
  }, [session, fetchSchema]);

  const handleCopyPlaceholder = async (
    tableName: string,
    columnName: string,
  ) => {
    const placeholder = `{${tableName}.${columnName}}`;

    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedPlaceholder(placeholder);
      setTimeout(() => setCopiedPlaceholder(null), 2000);

      if (onPlaceholderSelect) {
        onPlaceholderSelect(placeholder);
      }

      toast.success(`Placeholder gekopieerd: ${placeholder}`);
    } catch (error) {
      console.error("Failed to copy placeholder:", error);
      toast.error("Kon placeholder niet kopiëren");
    }
  };

  const handleCopyRelationPlaceholder = async (
    tableName: string,
    relationName: string,
    targetTable: string,
  ) => {
    const placeholder = `{${tableName}.${relationName}.*}`;

    try {
      await navigator.clipboard.writeText(placeholder);
      setCopiedPlaceholder(placeholder);
      setTimeout(() => setCopiedPlaceholder(null), 2000);

      if (onPlaceholderSelect) {
        onPlaceholderSelect(placeholder);
      }

      toast.success(`Relatie placeholder gekopieerd: ${placeholder}`);
      toast.info(
        `Vervang * met een eigenschap van ${targetTable} (bijv. {${tableName}.${relationName}.name})`,
      );
    } catch (error) {
      console.error("Failed to copy relation placeholder:", error);
      toast.error("Kon relatie placeholder niet kopiëren");
    }
  };

  const getTableDisplayName = (tableName: string): string => {
    return tableName;
  };

  const getColumnTypeColor = (
    dataType: string,
  ): "primary" | "secondary" | "success" | "warning" | "danger" => {
    if (dataType.includes("varchar") || dataType.includes("text"))
      return "primary";
    if (dataType.includes("int") || dataType.includes("number"))
      return "secondary";
    if (dataType.includes("bool")) return "success";
    if (dataType.includes("date") || dataType.includes("timestamp"))
      return "warning";
    return "danger";
  };

  const getRelationTypeColor = (
    relationType: string,
  ): "primary" | "secondary" | "success" | "warning" | "danger" => {
    switch (relationType) {
      case "ManyToOne":
        return "success";
      case "OneToMany":
        return "warning";
      case "OneToOne":
        return "primary";
      case "ManyToMany":
        return "secondary";
      default:
        return "danger";
    }
  };

  const filteredTables =
    schemaData?.detailedTables.filter(
      (table) =>
        table.tableName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getTableDisplayName(table.tableName)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        table.columns.some((col) =>
          col.columnName.toLowerCase().includes(searchTerm.toLowerCase()),
        ) ||
        table.relations?.some(
          (rel) =>
            rel.relationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rel.targetTable.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
    ) || [];

  if (loading) {
    return (
      <Card>
        <CardBody className="flex h-[600px] items-center justify-center py-8">
          <div className="flex flex-col items-center">
            <Spinner size="lg" />
            <p className="text-neutral-secondaryMid mt-4">
              Database schema laden...
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Dynamic Values Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={16} className="text-primary" />
            <h4 className="text-lg font-semibold">Dynamische Waarden</h4>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {[
                { key: "datumVandaag", description: "Huidige datum" },
                { key: "customText", description: "Aangepaste tekst" },
                { key: "bedrag", description: "Bedrag of prijs" },
                { key: "opmerking", description: "Extra opmerking" },
                { key: "referentie", description: "Referentienummer" },
                { key: "periode", description: "Tijdsperiode" },
              ].map((dynamicValue) => {
                const placeholder = `{waarde.${dynamicValue.key}}`;
                const isCopied = copiedPlaceholder === placeholder;

                return (
                  <Button
                    key={dynamicValue.key}
                    variant="bordered"
                    size="sm"
                    className="h-auto justify-between p-3"
                    onPress={() => {
                      navigator.clipboard.writeText(placeholder);
                      setCopiedPlaceholder(placeholder);
                      toast.success(`Placeholder gekopieerd: ${placeholder}`);
                      setTimeout(() => setCopiedPlaceholder(""), 2000);
                      if (onPlaceholderSelect) {
                        onPlaceholderSelect(placeholder);
                      }
                    }}
                    color={isCopied ? "success" : "default"}
                  >
                    <div className="flex flex-col items-start gap-1">
                      <code className="rounded bg-blue-50 px-2 py-1 font-mono text-xs text-blue-700">
                        {placeholder}
                      </code>
                      <span className="text-neutral-secondaryMid text-xs">
                        {dynamicValue.description}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isCopied ? (
                        <CheckCircle size={16} className="text-success" />
                      ) : (
                        <Copy size={16} className="text-neutral-secondaryMid" />
                      )}
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Database Tables Section */}
      <Card>
        <CardHeader>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <Database size={20} className="text-primary" />
              <h3 className="text-lg font-semibold">Database Placeholders</h3>
            </div>
            <Button
              size="sm"
              variant="light"
              onPress={fetchSchema}
              isLoading={loading}
            >
              Vernieuwen
            </Button>
          </div>
        </CardHeader>
        <CardBody className="flex h-[600px] flex-col space-y-4">
          <div className="flex flex-shrink-0 items-center gap-4">
            <Input
              placeholder="Zoek tabellen, kolommen of relaties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<MagnifyingGlass size={16} />}
              className="flex-1"
            />
            <Chip variant="flat" color="primary">
              {filteredTables.length}
            </Chip>
          </div>

          <Divider className="flex-shrink-0" />

          <div className="text-neutral-secondaryMid flex-shrink-0 text-sm">
            <p>
              Klik op een kolom of relatie om de placeholder te kopiëren.
              Relaties zijn groen gemarkeerd.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            <Accordion variant="splitted">
              {filteredTables.map((table) => (
                <AccordionItem
                  key={table.tableName}
                  aria-label={table.tableName}
                  title={
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Table size={16} className="text-secondary" />
                        <span className="font-medium">
                          {getTableDisplayName(table.tableName)}
                        </span>
                        <code className="rounded bg-neutral-100 px-2 py-1 text-xs">
                          {table.tableName}
                        </code>
                        <Chip size="sm" variant="flat" color="secondary">
                          {table.columns.length} kolommen
                        </Chip>
                        {table.relations && table.relations.length > 0 && (
                          <Chip size="sm" variant="flat" color="success">
                            {table.relations.length} relaties
                          </Chip>
                        )}
                      </div>
                      <Button
                        size="sm"
                        color="secondary"
                        variant="flat"
                        startContent={<Tree size={16} />}
                        onPress={() =>
                          navigate(`/entity-schema/${table.tableName}`)
                        }
                        className="ml-auto"
                      >
                        Visualiseer Schema
                      </Button>
                    </div>
                  }
                >
                  <div className="space-y-6">
                    {/* Columns Section */}
                    <div>
                      <p className="text-neutral-secondaryMid mb-3 text-sm">
                        <strong>Kolommen</strong> in de{" "}
                        <strong>{getTableDisplayName(table.tableName)}</strong>{" "}
                        tabel:
                      </p>

                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {table.columns.map((column) => {
                          const placeholder = `{${table.tableName}.${column.columnName}}`;
                          const isCopied = copiedPlaceholder === placeholder;
                          const isVirtual = column.dataType === "virtueel";

                          return (
                            <Button
                              key={column.columnName}
                              variant="bordered"
                              size="sm"
                              className={`h-auto justify-between p-3 ${
                                isVirtual ? "border-blue-200 bg-blue-50" : ""
                              }`}
                              onPress={() =>
                                handleCopyPlaceholder(
                                  table.tableName,
                                  column.columnName,
                                )
                              }
                            >
                              <div className="flex flex-col items-start">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {column.columnName}
                                  </span>
                                  {isVirtual && (
                                    <Chip
                                      size="sm"
                                      color="primary"
                                      variant="flat"
                                    >
                                      Standaard veld
                                    </Chip>
                                  )}
                                  {column.isPrimaryKey && (
                                    <Chip
                                      size="sm"
                                      color="warning"
                                      variant="flat"
                                    >
                                      PK
                                    </Chip>
                                  )}
                                  {column.isForeignKey && (
                                    <Chip
                                      size="sm"
                                      color="secondary"
                                      variant="flat"
                                    >
                                      FK
                                    </Chip>
                                  )}
                                </div>
                                <div className="mt-1 flex items-center gap-2">
                                  <Chip
                                    size="sm"
                                    color={getColumnTypeColor(column.dataType)}
                                    variant="flat"
                                  >
                                    {column.dataType}
                                  </Chip>
                                  {!column.isNullable && (
                                    <Chip
                                      size="sm"
                                      color="danger"
                                      variant="flat"
                                    >
                                      Required
                                    </Chip>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isCopied ? (
                                  <CheckCircle
                                    size={16}
                                    className="text-success"
                                  />
                                ) : (
                                  <Copy
                                    size={16}
                                    className="text-neutral-secondaryMid"
                                  />
                                )}
                              </div>
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Relations Section */}
                    {table.relations && table.relations.length > 0 && (
                      <div>
                        <Divider />
                        <p className="text-neutral-secondaryMid mt-4 mb-3 text-sm">
                          <strong>Relaties</strong> van de{" "}
                          <strong>
                            {getTableDisplayName(table.tableName)}
                          </strong>{" "}
                          tabel:
                        </p>

                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                          {table.relations.map((relation) => {
                            const placeholder = `{${table.tableName}.${relation.relationName}.*}`;
                            const isCopied = copiedPlaceholder === placeholder;

                            return (
                              <Button
                                key={relation.relationName}
                                variant="bordered"
                                size="sm"
                                className="h-auto justify-between border-green-200 bg-green-50 p-3"
                                onPress={() =>
                                  handleCopyRelationPlaceholder(
                                    table.tableName,
                                    relation.relationName,
                                    relation.targetTable,
                                  )
                                }
                              >
                                <div className="flex flex-col items-start">
                                  <div className="flex items-center gap-2">
                                    <ArrowRight
                                      size={14}
                                      className="text-green-600"
                                    />
                                    <span className="font-medium text-green-700">
                                      {relation.relationName}
                                    </span>
                                    <Chip
                                      size="sm"
                                      color={getRelationTypeColor(
                                        relation.relationType,
                                      )}
                                      variant="flat"
                                    >
                                      {relation.relationType}
                                    </Chip>
                                  </div>
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="text-xs text-green-600">
                                      → {relation.targetTable}
                                    </span>
                                    {!relation.isNullable && (
                                      <Chip
                                        size="sm"
                                        color="danger"
                                        variant="flat"
                                      >
                                        Required
                                      </Chip>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {isCopied ? (
                                    <CheckCircle
                                      size={16}
                                      className="text-success"
                                    />
                                  ) : (
                                    <Copy
                                      size={16}
                                      className="text-green-600"
                                    />
                                  )}
                                </div>
                              </Button>
                            );
                          })}
                        </div>

                        <div className="mt-3 space-y-2 rounded-lg bg-green-50 p-3">
                          <p className="text-xs text-green-700">
                            <strong>💡 Relatie Tip:</strong> Gebruik{" "}
                            <code>{`{${table.tableName}.relationName.property}`}</code>{" "}
                            om eigenschappen van gerelateerde entiteiten te
                            gebruiken.
                          </p>
                          <p className="text-xs text-green-700">
                            <strong>Voorbeeld:</strong>{" "}
                            <code>{`{${table.tableName}.${table.relations[0]?.relationName || "relation"}.name}`}</code>
                          </p>
                          <p className="text-xs text-green-700">
                            <strong>🔗 Geneste Relaties:</strong> Je kunt ook
                            meerdere relaties aan elkaar koppelen! Bijvoorbeeld:{" "}
                            <code>{`{unit.building.location.street}`}</code> of{" "}
                            <code>{`{contact.company.address.city}`}</code>
                          </p>
                          <p className="text-xs text-green-700">
                            <strong>✨ Tip:</strong> Er is geen limiet aan het
                            aantal niveaus - koppel zoveel relaties als je nodig
                            hebt!
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 rounded-lg bg-neutral-50 p-3">
                    <p className="text-neutral-secondaryMid text-xs">
                      <strong>Voorbeeld placeholders:</strong>
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {/* Column examples */}
                      {table.columns.slice(0, 2).map((column) => (
                        <code
                          key={column.columnName}
                          className="rounded border bg-white px-2 py-1 text-xs"
                        >
                          {`{${table.tableName}.${column.columnName}}`}
                        </code>
                      ))}

                      {/* Relation examples */}
                      {table.relations &&
                        table.relations.slice(0, 1).map((relation) => (
                          <code
                            key={relation.relationName}
                            className="rounded border border-green-200 bg-green-100 px-2 py-1 text-xs text-green-700"
                          >
                            {`{${table.tableName}.${relation.relationName}.name}`}
                          </code>
                        ))}

                      {(table.columns.length > 2 ||
                        (table.relations && table.relations.length > 1)) && (
                        <span className="text-neutral-secondaryMid text-xs">
                          +
                          {Math.max(0, table.columns.length - 2) +
                            Math.max(
                              0,
                              (table.relations?.length || 0) - 1,
                            )}{" "}
                          meer...
                        </span>
                      )}
                    </div>
                  </div>
                </AccordionItem>
              ))}
            </Accordion>

            {filteredTables.length === 0 && (
              <div className="py-8 text-center">
                <Database size={48} className="mx-auto mb-4 text-neutral-300" />
                <p className="text-neutral-secondaryMid">
                  Geen tabellen gevonden die overeenkomen met "{searchTerm}"
                </p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default DynamicPlaceholderBrowser;
